// ─── ArtifactsPanel (ocn-S241-P0, search wired in ocn-S246-P0) ────
// A standalone, mountable side surface that shows a project's
// scaffolding artifacts (architecture, worldview, conventions, etc.)
// beside the chat.
//
// Mount point for S242: place this as a side column in the chat-first
// layout next to <SubscriberChat projectId={...} /> or the chat panel.
// Example:
//
//   <div style={{ display: 'flex' }}>
//     <ChatPanel ... />
//     <ArtifactsPanel projectId={projectId} />
//   </div>
//
// Props:
//   projectId — required, the project to load artifacts for
//   width     — optional CSS width string (default "340px")
//   className — optional CSS class to apply to the root div

import { useState, useEffect, useCallback, useMemo } from "react";
import { FileText, RefreshCw, Loader2, LayoutList, X } from "lucide-react";
import type { ArtifactMeta, ArtifactContent } from "../lib/artifacts-api";
import { listProjectArtifacts, getProjectArtifact } from "../lib/artifacts-api";
import { ArtifactDocView } from "./ArtifactDocView";
import { api, type Artifact } from "../api";
import type { ArtifactFilterType } from "./ArtifactSearchFilter";
import { ArtifactSearchFilter } from "./ArtifactSearchFilter";

// ─── Key → display label mapping ─────────────────────────────────
// Provides friendly names for known artifact types. Unknown types fall
// back to title-casing the key, so new types surface without code changes.

const KEY_LABELS: Record<string, string> = {
  architecture: 'Architecture',
  worldview: 'Worldview',
  'agents-md': 'Agents',
  'routing-rules': 'Routing',
  blueprint: 'Blueprint',
  guardian: 'Guardian',
};

function labelForKey(key: string): string {
  return KEY_LABELS[key] ?? key
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Props ────────────────────────────────────────────────────────

export interface ArtifactsPanelProps {
  projectId: string;
  /** CSS width of the panel (default "340px") */
  width?: string;
  className?: string;
  /**
   * Additional filter types to expose in ArtifactSearchFilter even when
   * no artifact with that key is currently loaded (e.g. 'fix_library',
   * 'findings'). Passed through to ArtifactSearchFilter.extraFilterTypes.
   */
  extraFilterTypes?: ArtifactFilterType[];
  /** Use the project_artifacts API sections intended for the subscriber chat. */
  chatMode?: boolean;
  /**
   * ocn-S792-P0: when provided, renders an explicit "×" close affordance in
   * the panel's own header (next to Refresh) so collapsing the panel doesn't
   * rely solely on re-clicking whatever external toggle opened it.
   */
  onClose?: () => void;
}

// ocn-S792-P0: shared close-button style — 44px min tap target per the a11y
// requirement, visually compact via inline icon sizing.
const closeButtonStyle: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--text-dim)', borderRadius: 4,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  minWidth: 44, minHeight: 44,
  flexShrink: 0,
};

// ─── Component ───────────────────────────────────────────────────

function ProjectScaffoldArtifactsPanel({
  projectId,
  width = "340px",
  className,
  extraFilterTypes,
  onClose,
}: ArtifactsPanelProps) {
  const [artifacts, setArtifacts] = useState<ArtifactMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ArtifactContent | null>(null);
  const [docLoading, setDocLoading] = useState(false);

  // ── Search + filter state (ocn-S246-P0) ──────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [activeType, setActiveType] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      // Filtering is client-side: listProjectArtifacts fetches the full list.
      // Server-side seam: extend listProjectArtifacts(projectId, { q, type })
      // and the GET /api/projects/:id/artifacts route when server-side search
      // is needed (e.g. large artifact sets). See artifacts-api.ts.
      const data = await listProjectArtifacts(projectId);
      setArtifacts(data.artifacts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load artifacts');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
    setSelected(null);
    setSearchQuery('');
    setActiveType(null);
  }, [load]);

  // ── Derive data-driven filter types from artifacts present ────
  // Each artifact's key acts as its type identifier. Future types
  // (fix_library, findings) arrive via extraFilterTypes without
  // editing this component.
  const availableTypes = useMemo((): ArtifactFilterType[] => {
    const seen = new Set<string>();
    return artifacts
      .filter((a) => {
        if (seen.has(a.key)) return false;
        seen.add(a.key);
        return true;
      })
      .map((a) => ({ value: a.key, label: labelForKey(a.key) }));
  }, [artifacts]);

  // ── Combined text + type filter (client-side) ─────────────────
  // Searches: friendly_title (primary), key (secondary), source (path).
  // Content search would require pre-loading all artifact bodies; noted
  // as a server-side-search seam in the handoff.
  const filteredArtifacts = useMemo((): ArtifactMeta[] => {
    const q = searchQuery.trim().toLowerCase();
    return artifacts.filter((a) => {
      const matchesType = activeType === null || a.key === activeType;
      if (!matchesType) return false;
      if (!q) return true;
      return (
        a.friendly_title.toLowerCase().includes(q) ||
        a.key.toLowerCase().includes(q) ||
        (a.source?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [artifacts, searchQuery, activeType]);

  async function openArtifact(meta: ArtifactMeta) {
    setDocLoading(true);
    setSelected(null);
    try {
      const doc = await getProjectArtifact(projectId, meta.key);
      setSelected(doc);
    } catch (err) {
      setSelected({
        key: meta.key,
        friendly_title: meta.friendly_title,
        content: null,
        source: meta.source,
        updated_at: meta.updated_at,
      });
    } finally {
      setDocLoading(false);
    }
  }

  function formatDate(iso: string | null | undefined): string {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const now = new Date();
      const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
      if (diffDays === 0) return 'today';
      if (diffDays === 1) return 'yesterday';
      if (diffDays < 30) return `${diffDays}d ago`;
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  }

  return (
    <div
      className={className}
      style={{
        width,
        minWidth: width,
        maxWidth: width,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid var(--border)',
        background: 'var(--bg)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{
        padding: '10px 14px',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <LayoutList size={15} style={{ color: 'var(--orange)' }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
            Artifacts
          </span>
          {!loading && artifacts.length > 0 && (
            <span style={{
              fontSize: 10, color: 'var(--text-dim)',
              background: 'var(--surface2)', border: '1px solid var(--border)',
              borderRadius: 10, padding: '1px 6px',
            }}>
              {filteredArtifacts.length}
              {filteredArtifacts.length !== artifacts.length && (
                <span style={{ opacity: 0.6 }}>/{artifacts.length}</span>
              )}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <button
            onClick={() => { setSelected(null); setSearchQuery(''); setActiveType(null); load(); }}
            disabled={loading}
            title="Refresh"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-dim)', padding: 4, borderRadius: 4,
              display: 'flex', alignItems: 'center',
            }}
          >
            <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close artifacts panel"
              title="Close"
              style={closeButtonStyle}
            >
              <X size={15} />
            </button>
          )}
        </div>
      </div>

      {/* Search + filter (shown once artifacts have loaded and there's content) */}
      {!loading && !error && !selected && artifacts.length > 0 && (
        <ArtifactSearchFilter
          query={searchQuery}
          onQueryChange={(q) => { setSearchQuery(q); }}
          activeType={activeType}
          onTypeChange={(t) => { setActiveType(t); }}
          availableTypes={availableTypes}
          extraFilterTypes={extraFilterTypes}
        />
      )}

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: 12, paddingTop: selected || artifacts.length === 0 ? 12 : 0 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Loader2 size={18} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-dim)' }} />
          </div>
        ) : error ? (
          <div style={{
            padding: 12, borderRadius: 6, fontSize: 12, color: '#f87171',
            background: 'rgba(248, 113, 113, 0.06)', border: '1px solid rgba(248,113,113,0.2)',
          }}>
            {error}
          </div>
        ) : docLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Loader2 size={18} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-dim)' }} />
          </div>
        ) : selected ? (
          <ArtifactDocView
            title={selected.friendly_title}
            content={selected.content}
            source={selected.source}
            updatedAt={selected.updated_at}
            onBack={() => setSelected(null)}
          />
        ) : artifacts.length === 0 ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '40px 20px', textAlign: 'center',
            gap: 10,
          }}>
            <FileText size={28} style={{ color: 'var(--text-dim)', opacity: 0.3 }} />
            <p style={{ fontSize: 13, color: 'var(--text-dim)', margin: 0 }}>
              No artifacts yet
            </p>
            <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: 0, opacity: 0.7 }}>
              Run a sync to generate architecture docs, worldview, and agent conventions.
            </p>
          </div>
        ) : filteredArtifacts.length === 0 ? (
          /* No results for current search/filter */
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '32px 20px', textAlign: 'center', gap: 8,
          }}>
            <FileText size={22} style={{ color: 'var(--text-dim)', opacity: 0.25 }} />
            <p style={{ fontSize: 12, color: 'var(--text-dim)', margin: 0 }}>
              No artifacts match
              {searchQuery ? <> &ldquo;<em>{searchQuery}</em>&rdquo;</> : ' this filter'}.
            </p>
          </div>
        ) : (
          /* Artifact list */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {filteredArtifacts.map((art) => (
              <button
                key={art.key}
                onClick={() => openArtifact(art)}
                style={{
                  width: '100%', textAlign: 'left', background: 'var(--surface2)',
                  border: '1px solid var(--border)', borderRadius: 7,
                  padding: '9px 11px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 10,
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface3)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface2)')}
              >
                <FileText size={14} style={{ color: 'var(--orange)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 500, color: 'var(--text)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {art.friendly_title}
                  </div>
                  <div style={{
                    fontSize: 10, color: 'var(--text-dim)', marginTop: 1,
                    fontFamily: 'var(--font-mono)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {art.source}
                    {art.updated_at ? ` · ${formatDate(art.updated_at)}` : ''}
                  </div>
                </div>
                <span style={{ fontSize: 14, color: 'var(--text-dim)', flexShrink: 0 }}>›</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// The chat variant composes this established mount point with the existing
// search filter, while keeping the original scaffold-only panel available to
// its existing callers.
type ChatSection = 'generated' | 'uploaded' | 'chat' | 'findings' | 'specs';
const CHAT_SECTION_ORDER: ChatSection[] = ['generated', 'uploaded', 'chat', 'findings', 'specs'];
const CHAT_SECTION_LABELS: Record<ChatSection, string> = {
  generated: 'Generated by the build', uploaded: 'Uploaded by you', chat: 'Created in chat',
  findings: 'Findings & reports', specs: 'Specs',
};

function chatArtifactName(artifact: Artifact): string { return artifact.display_name || artifact.filename; }
function chatSectionFor(artifact: Artifact): ChatSection {
  // Classify from API origin metadata, never from a fixed scaffold filename list.
  const origin = `${artifact.filename} ${artifact.display_name ?? ''} ${artifact.repo_path ?? ''}`.toLowerCase();
  if (/\b(finding|findings|report|audit|guardian)\b/.test(origin)) return 'findings';
  if (/\b(spec|prd|requirements)\b/.test(origin)) return 'specs';
  if (artifact.tab === 'input') return 'uploaded';
  return artifact.repo_path ? 'generated' : 'chat';
}
function chatUpdatedAt(value: string | null): string {
  if (!value) return 'Updated time unavailable';
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? 'Updated time unavailable' : `Current as of ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function SubscriberChatArtifactsPanel({ projectId, width, className, extraFilterTypes, onClose }: ArtifactsPanelProps) {
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [activeType, setActiveType] = useState<string | null>(null);
  const [selected, setSelected] = useState<Artifact | null>(null);
  const [detail, setDetail] = useState<Artifact | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setFailed(false);
    try { setArtifacts((await api.getArtifacts(projectId)) ?? []); }
    catch { setArtifacts([]); setFailed(true); }
    finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { void load(); setSelected(null); setDetail(null); setQuery(''); setActiveType(null); }, [load]);

  const visible = useMemo(() => artifacts.filter((artifact) => {
    const search = query.trim().toLowerCase();
    return (activeType === null || chatSectionFor(artifact) === activeType)
      && (!search || chatArtifactName(artifact).toLowerCase().includes(search) || artifact.filename.toLowerCase().includes(search));
  }), [activeType, artifacts, query]);
  const open = useCallback(async (artifact: Artifact) => {
    setSelected(artifact); setDetail(null);
    try { setDetail(await api.getArtifactDetail(artifact.id)); } catch { /* keep chat usable if one document fails */ }
  }, []);

  return <aside className={className} aria-label="Project artifacts" style={{ width: width ?? '340px', minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface)', borderLeft: '1px solid var(--border)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px 8px' }}><strong style={{ fontSize: 14 }}>Project artifacts</strong><div style={{ display: 'flex', alignItems: 'center', gap: 2 }}><button type="button" aria-label="Refresh artifacts" onClick={() => void load()} style={chatPlainButton}><RefreshCw size={14} /></button>{onClose && <button type="button" aria-label="Close artifacts panel" title="Close" onClick={onClose} style={closeButtonStyle}><X size={15} /></button>}</div></div>
    {selected ? <div style={{ overflowY: 'auto', padding: 12 }}>{detail ? <ArtifactDocView title={chatArtifactName(detail)} content={detail.content ?? null} source={detail.repo_path ?? selected.filename} updatedAt={detail.updated_at} onBack={() => { setSelected(null); setDetail(null); }} /> : <p style={chatEmptyStyle}>Document unavailable. Try refreshing the drawer.</p>}</div> : <>
      <ArtifactSearchFilter query={query} onQueryChange={setQuery} activeType={activeType} onTypeChange={setActiveType} availableTypes={CHAT_SECTION_ORDER.map((value) => ({ value, label: CHAT_SECTION_LABELS[value] }))} extraFilterTypes={extraFilterTypes} />
      <div style={{ overflowY: 'auto', padding: '0 12px 14px' }}>
        {loading ? <p style={chatEmptyStyle}><Loader2 size={18} className="spin" /> Loading artifacts…</p> : failed ? <p role="status" style={chatEmptyStyle}>Artifacts could not be loaded. The chat is still available; try refreshing.</p> : visible.length === 0 ? <p style={chatEmptyStyle}>{artifacts.length ? 'No artifacts match your search.' : 'This project does not have any artifacts yet.'}</p> : CHAT_SECTION_ORDER.map((section) => {
          const rows = visible.filter((artifact) => chatSectionFor(artifact) === section);
          return rows.length ? <section key={section} style={{ marginBottom: 16 }}><h2 style={{ margin: '8px 0', color: 'var(--text-dim)', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{CHAT_SECTION_LABELS[section]}</h2>{rows.map((artifact) => <button type="button" key={artifact.id} onClick={() => void open(artifact)} style={chatArtifactButton}><FileText size={14} style={{ color: 'var(--orange)', flexShrink: 0 }} /><span style={{ minWidth: 0, flex: 1 }}><span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chatArtifactName(artifact)}</span><small style={{ color: 'var(--text-dim)' }}>{chatUpdatedAt(artifact.updated_at)}</small></span></button>)}</section> : null;
        })}
      </div>
    </>}
  </aside>;
}

export function ArtifactsPanel(props: ArtifactsPanelProps) {
  return props.chatMode ? <SubscriberChatArtifactsPanel {...props} /> : <ProjectScaffoldArtifactsPanel {...props} />;
}
const chatPlainButton = { border: 'none', background: 'transparent', color: 'var(--text-dim)', cursor: 'pointer', display: 'inline-flex', padding: 4 } as const;
const chatArtifactButton = { width: '100%', border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', borderRadius: 6, cursor: 'pointer', padding: '9px 10px', marginBottom: 5, textAlign: 'left', display: 'flex', gap: 8, alignItems: 'center' } as const;
const chatEmptyStyle = { color: 'var(--text-dim)', fontSize: 13, padding: '20px 8px', display: 'flex', gap: 8, alignItems: 'center' } as const;
